import { useCallback, useEffect, useState } from "react";

import { CouponDto } from "services/coupons/restModels";
import { CouponRequestDto } from "services/coupons/restRequests";
import { useConstants } from "shared/hooks/useConstants";
import { useCoupons } from "shared/hooks/useCoupons";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuOptions } from "shared/hooks/useMenuOptions";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { useNotificationSnackbar } from "shared/ui";

import { COUPONS_PAGE_SIZE } from "features/Coupons/constants";
import { useCouponMutations } from "features/Coupons/hooks/useCouponMutations";
import { CouponsDialog, CouponsState, CouponsTypeFilter } from "features/Coupons/types";

export const useCouponsState = (): CouponsState => {
    const locale = useLocale();
    const currencyId = useCurrencyId();

    const {
        menuOptions,
        loading: menusLoading,
        error: menusError,
    } = useMenuOptions();
    const {
        constants,
        loading: constantsLoading,
        error: constantsError,
    } = useConstants();

    const [menuId, setMenuIdState] = useState<number | null>(null);
    const [typeFilter, setTypeFilterState] = useState<CouponsTypeFilter>(null);
    const [page, setPageState] = useState<number>(1);
    const [dialog, setDialog] = useState<CouponsDialog>({ kind: "none" });

    const notify = useNotificationSnackbar();
    const { saving, saveCoupon, removeCoupon } = useCouponMutations(notify);

    const {
        coupons,
        total,
        loading: couponsLoading,
        error: couponsError,
        refetch,
    } = useCoupons(menuId, typeFilter, (page - 1) * COUPONS_PAGE_SIZE, COUPONS_PAGE_SIZE);

    // Меню в кабинете пока выбирается на самом экране (в шапку селект переедет позже) —
    // до первого выбора берём первое меню филиала.
    useEffect(() => {
        if (menuId !== null || menuOptions.length === 0) return;

        setMenuIdState(menuOptions[0].id);
    }, [menuId, menuOptions]);

    const currencies = constants?.currencies;

    const currencyBy = useCallback(
        (id: string | null) =>
            (currencies || []).find((item) => item.id === (id ?? currencyId)) || null,
        [currencies, currencyId]
    );

    const formatterFor = useCallback(
        (id: string | null) => createCurrencyFormatter(currencyBy(id), locale),
        [currencyBy, locale]
    );

    const symbolFor = useCallback(
        (id: string | null) => {
            const currency = currencyBy(id);

            return currency ? currency.shortName[locale] || "" : "";
        },
        [currencyBy, locale]
    );

    // Смена меню и фильтра возвращает на первую страницу: номер прежней страницы к новой
    // выборке отношения не имеет, а на коротком результате она просто окажется пустой.
    const setMenuId = useCallback((next: number) => {
        setMenuIdState(next);
        setPageState(1);
    }, []);

    const setTypeFilter = useCallback((next: CouponsTypeFilter) => {
        setTypeFilterState(next);
        setPageState(1);
    }, []);

    const openCreateDialog = useCallback(() => setDialog({ kind: "createCoupon" }), []);

    const openEditDialog = useCallback(
        (coupon: CouponDto) => setDialog({ kind: "editCoupon", coupon }),
        []
    );

    const openDeleteDialog = useCallback(
        (coupon: CouponDto) => setDialog({ kind: "deleteCoupon", coupon }),
        []
    );

    const closeDialog = useCallback(() => setDialog({ kind: "none" }), []);

    /*
    * Перечитать список после мутации. Переход на другую страницу сам утащит новые данные
    * (страница — зависимость эффекта в useCoupons), поэтому refetch зовём только когда
    * номер страницы не меняется — иначе запрос ушёл бы дважды.
    * */
    const reload = useCallback(
        (targetPage: number) => {
            if (targetPage === page) {
                refetch();
                return;
            }

            setPageState(targetPage);
        },
        [page, refetch]
    );

    const submitCoupon = useCallback(
        async (values: CouponRequestDto) => {
            if (menuId === null) return;

            const editedCouponId = dialog.kind === "editCoupon" ? dialog.coupon.id : null;
            const saved = await saveCoupon(values, menuId, editedCouponId);

            if (!saved) return;

            closeDialog();
            // Новый промокод — самый свежий, а список отсортирован от новых к старым:
            // после создания он лежит на первой странице.
            reload(editedCouponId === null ? 1 : page);
        },
        [closeDialog, dialog, menuId, page, reload, saveCoupon]
    );

    const confirmDelete = useCallback(async () => {
        if (dialog.kind !== "deleteCoupon") return;

        const removed = await removeCoupon(dialog.coupon.id);

        if (!removed) return;

        closeDialog();
        // Удалили единственную строку страницы — она станет пустой, отступаем назад.
        reload(coupons.length === 1 && page > 1 ? page - 1 : page);
    }, [closeDialog, coupons.length, dialog, page, reload, removeCoupon]);

    return {
        menuOptions,
        menuId,
        setMenuId,

        coupons,
        total,
        page,
        pageSize: COUPONS_PAGE_SIZE,
        setPage: setPageState,

        typeFilter,
        setTypeFilter,

        // Константы нужны для форматирования суммы скидки — гейтим каркас и на них.
        loading: menusLoading || constantsLoading,
        error: menusError || constantsError,
        listLoading: couponsLoading,
        listError: couponsError,
        saving,

        locale,
        formatterFor,
        symbolFor,

        dialog,
        openCreateDialog,
        openEditDialog,
        openDeleteDialog,
        closeDialog,

        submitCoupon,
        confirmDelete,

        notify,
    };
};
